import { useState } from 'react';
import { X, Check, AlertTriangle, Info, Package, FileText, TrendingUp } from 'lucide-react';
import { clsx } from 'clsx';
import { GlassPanel } from './GlassPanel';

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'success' | 'error';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionLabel?: string;
  actionHref?: string;
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const mockNotifications: Notification[] = [
  {
    id: '1',
    type: 'warning',
    title: 'Low Stock Alert',
    message: '5 items are below reorder point. Consider placing orders.',
    timestamp: new Date(Date.now() - 1000 * 60 * 15),
    read: false,
    actionLabel: 'View Items',
    actionHref: '/items',
  },
  {
    id: '2',
    type: 'info',
    title: '3 Pending Deliveries',
    message: 'You have 3 purchase orders awaiting receiving verification.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    read: false,
    actionLabel: 'View Queue',
    actionHref: '/receiving',
  },
  {
    id: '3',
    type: 'success',
    title: 'Invoice Processed',
    message: 'Purchase invoice INV-2024-0145 has been posted successfully.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
    read: true,
  },
  {
    id: '4',
    type: 'info',
    title: 'Weekly Report Ready',
    message: 'Your cost analytics report for the week is now available.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    read: true,
    actionLabel: 'View Report',
    actionHref: '/procurement/cost-analytics',
  },
  {
    id: '5',
    type: 'warning',
    title: 'Supplier Payment Due',
    message: 'Payment to Fresh Farm Produce Ltd (€3,420.50) is due tomorrow.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    read: true,
    actionLabel: 'View Invoices',
    actionHref: '/procurement/invoices',
  },
];

function getIcon(type: string) {
  switch (type) {
    case 'warning':
      return AlertTriangle;
    case 'success':
      return Check;
    case 'error':
      return AlertTriangle;
    default:
      return Info;
  }
}

function getTypeColor(type: string) {
  switch (type) {
    case 'warning':
      return 'text-warning-400 bg-warning-500/20';
    case 'success':
      return 'text-success-400 bg-success-500/20';
    case 'error':
      return 'text-error-400 bg-error-500/20';
    default:
      return 'text-primary-400 bg-primary-500/20';
  }
}

function formatTimestamp(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}

export function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const [notifications, setNotifications] = useState(mockNotifications);

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" onClick={onClose}>
      <div
        className="absolute right-4 top-20 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <GlassPanel className="p-0 max-h-[600px] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div>
              <h3 className="text-lg font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <p className="text-sm text-white/60">{unreadCount} unread</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/5 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-12 text-center">
                <Check className="w-12 h-12 text-white/40 mx-auto mb-3" />
                <p className="text-white/60">All caught up!</p>
                <p className="text-sm text-white/40 mt-1">No new notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {notifications.map((notification) => {
                  const Icon = getIcon(notification.type);

                  return (
                    <div
                      key={notification.id}
                      className={clsx(
                        'p-4 hover:bg-white/5 transition-colors',
                        !notification.read && 'bg-primary-500/5'
                      )}
                    >
                      <div className="flex gap-3">
                        <div
                          className={clsx(
                            'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
                            getTypeColor(notification.type)
                          )}
                        >
                          <Icon className="w-4 h-4" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="text-sm font-medium text-white">
                              {notification.title}
                            </h4>
                            {!notification.read && (
                              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-400" />
                            )}
                          </div>

                          <p className="text-sm text-white/70 mb-2">
                            {notification.message}
                          </p>

                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">
                              {formatTimestamp(notification.timestamp)}
                            </span>

                            {notification.actionLabel && notification.actionHref && (
                              <a
                                href={notification.actionHref}
                                onClick={() => {
                                  markAsRead(notification.id);
                                  onClose();
                                }}
                                className="text-xs text-primary-400 hover:text-primary-300 font-medium transition-colors"
                              >
                                {notification.actionLabel} →
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}
